import { apiClient, apiClientEnvelope } from '@/lib/api-client';
import type {
  AcceptLine,
  SiteOrder,
  SiteOrderDetail,
  SiteOrderListResponse,
  SiteOrderStatus,
} from '@/types/site-order';

/**
 * Online orders from the tenant's public storefront.
 *
 * ONE API, two clients — the same endpoints back `apps/pos/src/services/site-orders.service.ts`,
 * because the same shopkeeper works the queue from whichever screen is in front
 * of them. The till is where most acceptances happen; this is where an owner
 * reviews the day, chases what nobody has touched, and sees what was rejected
 * and why.
 *
 * Thin wrappers over `apiClient`, so the 401 → refresh → retry cycle and the
 * structured `details` a 409 carries come for free.
 */

const BASE = '/site-orders';

function query(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

export const siteOrderService = {
  list(
    params: {
      page?: number;
      limit?: number;
      scope?: 'open' | 'sales' | 'all';
      status?: SiteOrderStatus;
      location_id?: string;
    } = {},
  ) {
    // `apiClientEnvelope`, not `apiClient` — the list's `meta` carries the
    // per-status counts the tabs render, and `apiClient` would strip them.
    return apiClientEnvelope<SiteOrderListResponse>(`${BASE}${query(params)}`);
  },

  get(id: string) {
    return apiClient<SiteOrderDetail>(`${BASE}/${id}`);
  },

  /** No `lines` means "all of it" — the common case, which must not make
   *  anyone enumerate anything. Quantities may go down and never up. */
  accept(id: string, lines?: AcceptLine[]) {
    return apiClient<SiteOrderDetail>(`${BASE}/${id}/accept`, {
      method: 'POST',
      body: lines?.length ? { lines } : {},
    });
  },

  /** The reason is shown to the customer, so the API requires one. */
  reject(id: string, reason: string) {
    return apiClient<SiteOrder>(`${BASE}/${id}/reject`, { method: 'POST', body: { reason } });
  },

  markReady(id: string, staffNote?: string) {
    return apiClient<SiteOrder>(`${BASE}/${id}/ready`, {
      method: 'PATCH',
      body: staffNote ? { staff_note: staffNote } : {},
    });
  },

  markContacted(id: string, staffNote?: string) {
    return apiClient<SiteOrder>(`${BASE}/${id}/contacted`, {
      method: 'PATCH',
      body: staffNote ? { staff_note: staffNote } : {},
    });
  },

  closeEnquiry(id: string, won: boolean, staffNote?: string) {
    return apiClient<SiteOrder>(`${BASE}/${id}/close`, {
      method: 'PATCH',
      body: { won, ...(staffNote ? { staff_note: staffNote } : {}) },
    });
  },

  /**
   * Re-link bills an interrupted acceptance left orphaned.
   *
   * MANAGER-only, and an operational lever rather than queue work. A cron does
   * the same sweep every minute; this exists for the case where somebody is
   * looking at a stuck order right now and wants it fixed before they finish
   * reading the sentence.
   */
  reconcile() {
    return apiClient<{ repaired: number }>(`${BASE}/reconcile`, { method: 'POST' });
  },
};
